import { $themeConfig } from '../config/theme.config';

export const initialState = {
    isDarkMode: false,
    mainLayout: 'app',
    theme: 'light',
    menu: $themeConfig.menu,
    layout: 'full',
    rtlClass: 'ltr',
    animation: '',
    navbar: 'navbar-sticky',
    locale: 'en',
    sidebar: false,
    languageList: [
        { code: 'zh', name: 'Chinese' },
        { code: 'da', name: 'Danish' },
        { code: 'en', name: 'English' },
        { code: 'fr', name: 'French' },
        { code: 'de', name: 'German' },
        { code: 'el', name: 'Greek' },
        { code: 'hu', name: 'Hungarian' },
        { code: 'it', name: 'Italian' },
        { code: 'ja', name: 'Japanese' },
        { code: 'pl', name: 'Polish' },
        { code: 'pt', name: 'Portuguese' },
        { code: 'ru', name: 'Russian' },
        { code: 'es', name: 'Spanish' },
        { code: 'sv', name: 'Swedish' },
        { code: 'tr', name: 'Turkish' },
        { code: 'ae', name: 'Arabic' },
    ],
    isShowMainLoader: true,
    semidark: false,
};

type IndexState = typeof initialState;

function resolveDarkMode(theme: string, current: boolean): boolean {
    if (theme === 'light') {
        return false;
    }
    if (theme === 'dark') {
        return true;
    }
    if (theme === 'system') {
        return !!globalThis.window?.matchMedia?.('(prefers-color-scheme: dark)').matches;
    }
    return current;
}

function enableBodyDarkClass(): void {
    document.querySelector('body')?.classList.add('dark');
}

function disableBodyDarkClass(): void {
    document.querySelector('body')?.classList.remove('dark');
}

function applyBodyDarkClass(isDarkMode: boolean): void {
    if (isDarkMode) {
        enableBodyDarkClass();
        return;
    }
    disableBodyDarkClass();
}

function handleToggleDirection(state: IndexState, payload: string) {
    localStorage.setItem('direction', payload);
    document.querySelector('html')?.setAttribute('dir', payload || 'ltr');
    return { ...state, direction: payload };
}

function handleToggleTheme(state: IndexState, payload: string) {
    const theme = payload || state.theme;
    localStorage.setItem('theme', theme);
    const isDarkMode = resolveDarkMode(theme, state.isDarkMode || false);
    applyBodyDarkClass(isDarkMode);
    return { ...state, theme, isDarkMode };
}

function handleToggleAnimation(state: IndexState, payload: string) {
    const animation = payload?.trim();
    localStorage.setItem('animation', animation);
    if (animation) {
        const eleanimation: Element | null = document.querySelector('.animation');
        eleanimation?.classList.add('animate__animated', animation);
    }
    return { ...state, animation };
}

function handleToggleRTL(state: IndexState, payload: string) {
    const rtlClass = payload || state.rtlClass;
    localStorage.setItem('rtlClass', rtlClass);
    document.querySelector('html')?.setAttribute('dir', rtlClass || 'ltr');
    return { ...state, rtlClass };
}

export function indexReducer(state = initialState, action: any) {
    const type = action.type;
    const payload = action.payload;

    switch (type) {
        case 'toggleDirection':
            return handleToggleDirection(state, payload);
        case 'toggleMainLoader':
            return { ...state, isShowMainLoader: payload };
        case 'setMainLayout':
            return { ...state, mainLayout: payload };
        case 'toggleTheme':
            return handleToggleTheme(state, payload);
        case 'toggleMenu': {
            const menu = payload || state.menu;
            localStorage.setItem('menu', menu);
            return { ...state, sidebar: false, menu };
        }
        case 'toggleLayout': {
            const layout = payload || state.layout;
            localStorage.setItem('layout', layout);
            return { ...state, layout };
        }
        case 'toggleRTL':
            return handleToggleRTL(state, payload);
        case 'toggleAnimation':
            return handleToggleAnimation(state, payload);
        case 'toggleNavbar': {
            const navbar = payload || state.navbar;
            localStorage.setItem('navbar', navbar);
            return { ...state, navbar };
        }
        case 'toggleSemidark': {
            const semidark = payload || false;
            localStorage.setItem('semidark', semidark);
            return { ...state, semidark };
        }
        case 'toggleLocale': {
            const locale = payload || state.locale;
            localStorage.setItem('i18n_locale', locale);
            return { ...state, locale };
        }
        case 'toggleSidebar':
            return { ...state, sidebar: !state.sidebar };
        default:
            return state;
    }
}
